import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import { ensureWebToken } from "../services/auth";
import { getRestaurantById } from "../services/restaurant";
import { checkReservation } from "../services/reservationRequest";
import { IRestaurant } from "../models/IRestaurant";
import { IReservationCheckResponse } from "../models/IReservationCheck";
import { RESTAURANT_ID, S3_IMAGE_BASE_URL } from "../config/restaurantConfig";
import { extractApiErrorMessage } from "../utils/apiError";
import CancelReservation from "./CancelReservation";

const CANCELLABLE_STATUSES = ["Confirmed", "Pending"];

interface Props {
  reservationId: string;
}

const STATUS_LABELS: Record<string, string> = {
  Confirmed: "Onaylandı",
  Pending: "Beklemede",
  Cancelled: "İptal Edildi",
  Completed: "Tamamlandı",
  NoShow: "Gelmedi",
};

const getCountdownLabel = (startsAtUtc: string): string => {
  const totalMinutes = Math.max(0, Math.floor((new Date(startsAtUtc).getTime() - Date.now()) / 60000));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days >= 1) {
    return hours > 0 ? `Rezervasyonunuza ${days} gün ${hours} saat kaldı` : `Rezervasyonunuza ${days} gün kaldı`;
  }
  if (hours >= 1) {
    return minutes > 0 ? `Rezervasyonunuza ${hours} saat ${minutes} dakika kaldı` : `Rezervasyonunuza ${hours} saat kaldı`;
  }
  return `Rezervasyonunuza ${minutes} dakika kaldı`;
};

const formatDateTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      timeZone: "Europe/Istanbul",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function ReservationTicket({ reservationId }: Props) {
  const [restaurant, setRestaurant] = useState<IRestaurant | null>(null);
  const [checkResult, setCheckResult] = useState<IReservationCheckResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState("");
  const exportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ensureWebToken()
      .then(() =>
        Promise.allSettled([getRestaurantById(RESTAURANT_ID), checkReservation(reservationId)])
      )
      .then(([restaurantRes, checkRes]) => {
        if (restaurantRes.status === "fulfilled") setRestaurant(restaurantRes.value.data);

        if (checkRes.status === "fulfilled") {
          setCheckResult(checkRes.value.data);
        } else {
          setError(extractApiErrorMessage(checkRes.reason, "Rezervasyon bulunamadı."));
        }
      })
      .catch(() => setError("Rezervasyon bilgileri yüklenirken bir sorun oluştu. Lütfen tekrar deneyin."))
      .finally(() => setLoading(false));
  }, [reservationId]);

  const refetchReservationStatus = () => {
    checkReservation(reservationId)
      .then((res) => setCheckResult(res.data))
      .catch(() => {});
  };

  const handleShareOrSave = async () => {
    if (!exportRef.current || sharing) return;
    setSharing(true);
    setShareError("");
    try {
      const dataUrl = await toPng(exportRef.current, {
        pixelRatio: 2,
        backgroundColor: "#1c1a17",
        // Restoran logosu S3'te CORS'a kapalı; dışa aktarırken bu resmi atlıyoruz ki tüm işlem CORS hatasıyla başarısız olmasın.
        filter: (node) => !(node instanceof HTMLImageElement && node.classList.contains("rr-ticket-logo")),
      });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "rezervasyon-bileti.png", { type: "image/png" });

      const nav = navigator as Navigator & { canShare?: (data?: { files?: File[] }) => boolean };
      if (nav.canShare && nav.canShare({ files: [file] }) && navigator.share) {
        await navigator.share({
          files: [file],
          title: "Rezervasyon Bileti",
          text: restaurant?.restourantName ?? "Rezervasyon Bileti",
        });
      } else {
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = "rezervasyon-bileti.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      if ((err as { name?: string })?.name !== "AbortError") {
        setShareError("Bilet resmi oluşturulurken bir sorun oluştu. Lütfen tekrar deneyin.");
      }
    } finally {
      setSharing(false);
    }
  };

  const showActions = !loading && !error && Boolean(checkResult);

  return (
    <div className="rr-ticket-page">
      <div className="rr-ticket-export-wrap" ref={exportRef}>
        <div className="rr-ticket-card">
          <div className="rr-ticket-notch rr-ticket-notch-left" />
          <div className="rr-ticket-notch rr-ticket-notch-right" />

          <div className="rr-ticket-header">
            {restaurant?.restourantLogoPath && (
              <img
                className="rr-ticket-logo"
                src={S3_IMAGE_BASE_URL + restaurant.restourantLogoPath}
                alt={restaurant.restourantName}
              />
            )}
            <div className="rr-ticket-restaurant-name">{restaurant?.restourantName ?? "Restoran"}</div>
            <div className="rr-ticket-title">Rezervasyon Bileti</div>
          </div>

          {loading ? (
            <div className="rr-ticket-loading">
              <div className="spinner-border" style={{ color: "#b8934a" }} role="status" />
            </div>
          ) : error ? (
            <div className="rr-ticket-error">
              <i className="bi bi-x-circle-fill"></i>
              <p>{error}</p>
            </div>
          ) : (
            checkResult && (
              <>
                {checkResult.status === "Confirmed" && new Date(checkResult.startsAtUtc).getTime() > Date.now() ? (
                  <div className="rr-ticket-status upcoming">
                    <i className="bi bi-hourglass-split"></i>
                    {getCountdownLabel(checkResult.startsAtUtc)}
                  </div>
                ) : (
                  <>
                    <div className={`rr-ticket-status ${checkResult.isValid ? "valid" : "invalid"}`}>
                      <i className={`bi ${checkResult.isValid ? "bi-check-circle-fill" : "bi-exclamation-triangle-fill"}`}></i>
                      {checkResult.isValid ? "Geçerli Rezervasyon" : "Geçersiz Rezervasyon"}
                    </div>
                    {!checkResult.isValid && checkResult.reason && (
                      <p className="rr-ticket-reason">{checkResult.reason}</p>
                    )}
                  </>
                )}

                <div className="rr-ticket-divider" />

                <div className="rr-ticket-qr-wrap">
                  <QRCodeSVG value={checkResult.id} size={168} bgColor="#faf7f2" fgColor="#1c1a17" />
                </div>

                <div className="rr-ticket-divider" />

                <div className="rr-ticket-details">
                  <div className="rr-ticket-row">
                    <span className="rr-ticket-label">Ad Soyad</span>
                    <span className="rr-ticket-value">{checkResult.contactName}</span>
                  </div>
                  <div className="rr-ticket-row">
                    <span className="rr-ticket-label">Tarih &amp; Saat</span>
                    <span className="rr-ticket-value">{formatDateTime(checkResult.startsAtUtc)}</span>
                  </div>
                  <div className="rr-ticket-row">
                    <span className="rr-ticket-label">Kişi Sayısı</span>
                    <span className="rr-ticket-value">{checkResult.partySize}</span>
                  </div>
                  {(checkResult.tableLabel || checkResult.tableNumber) && (
                    <div className="rr-ticket-row">
                      <span className="rr-ticket-label">Masa</span>
                      <span className="rr-ticket-value">
                        {checkResult.tableLabel}
                        {checkResult.tableLabel && checkResult.tableNumber ? " · " : ""}
                        {checkResult.tableNumber ? `Masa ${checkResult.tableNumber}` : ""}
                      </span>
                    </div>
                  )}
                  <div className="rr-ticket-row">
                    <span className="rr-ticket-label">Durum</span>
                    <span className="rr-ticket-value">
                      {STATUS_LABELS[checkResult.status] ?? checkResult.status}
                    </span>
                  </div>
                </div>
              </>
            )
          )}
        </div>
      </div>

      {showActions && (
        <div className="rr-ticket-actions">
          <button type="button" className="rr-btn-gold rr-ticket-share-btn" onClick={handleShareOrSave} disabled={sharing}>
            {sharing ? (
              <span className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <i className="bi bi-share-fill"></i>
            )}
            {sharing ? "Hazırlanıyor..." : "Bileti Paylaş / Kaydet"}
          </button>
          {shareError && <p className="rr-ticket-share-error">{shareError}</p>}

          {checkResult && CANCELLABLE_STATUSES.includes(checkResult.status) && (
            <CancelReservation reservationId={checkResult.id} onCancelled={refetchReservationStatus} />
          )}
        </div>
      )}

      <a href="/" className="rr-ticket-back-link">
        <i className="bi bi-arrow-left"></i>
        Anasayfaya Dön
      </a>
    </div>
  );
}
